import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { baseUrl } from '../../constants';

@Injectable({ providedIn: 'root' })
export class CommunicationService {
  private apiUrl = `${baseUrl}Communication`;
  private hub?: signalR.HubConnection;
  private messageSubject = new Subject<any>();
  private existingCallParticipantsSubject = new Subject<any[]>();
  private callParticipantJoinedSubject = new Subject<any>();
  private callParticipantLeftSubject = new Subject<string>();
  private incomingCallSubject = new Subject<any>();
  private callAcceptedSubject = new Subject<any>();
  private callRejectedSubject = new Subject<any>();
  private offerSubject = new Subject<any>();
  private answerSubject = new Subject<any>();
  private iceCandidateSubject = new Subject<any>();
  readonly messages$ = this.messageSubject.asObservable();
  readonly existingCallParticipants$ = this.existingCallParticipantsSubject.asObservable();
  readonly callParticipantJoined$ = this.callParticipantJoinedSubject.asObservable();
  readonly callParticipantLeft$ = this.callParticipantLeftSubject.asObservable();
  readonly incomingCalls$ = this.incomingCallSubject.asObservable();
  readonly callAccepted$ = this.callAcceptedSubject.asObservable();
  readonly callRejected$ = this.callRejectedSubject.asObservable();
  readonly offers$ = this.offerSubject.asObservable();
  readonly answers$ = this.answerSubject.asObservable();
  readonly iceCandidates$ = this.iceCandidateSubject.asObservable();

  constructor(private http: HttpClient) {}

  private get headers() {
    const token = localStorage.getItem('token');
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      })
    };
  }

  getThreads(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/threads`, this.headers);
  }

  getBadgeCounts(): Observable<{ unreadChats: number; missedCalls: number; total: number }> {
    return this.http.get<{ unreadChats: number; missedCalls: number; total: number }>(`${this.apiUrl}/badge-counts`, this.headers);
  }

  createDirectThread(staffId: number): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/threads/direct`, { staffId }, this.headers);
  }

  createGroupThread(title: string, staffIds: number[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/threads/group`, { title, staffIds }, this.headers);
  }

  getMessages(threadId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/threads/${threadId}/messages`, this.headers);
  }

  sendMessage(threadId: number, message: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/threads/${threadId}/messages`, { message }, this.headers);
  }

  markThreadRead(threadId: number): Observable<any> {
    return this.http.put(`${this.apiUrl}/threads/${threadId}/read`, {}, this.headers);
  }

  getCalls(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/calls`, this.headers);
  }

  markMissedCallsSeen(): Observable<any> {
    return this.http.put(`${this.apiUrl}/calls/missed/seen`, {}, this.headers);
  }

  startCall(payload: { callType: string; callScope: string; title?: string | null; staffIds: number[] }): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/calls`, payload, this.headers);
  }

  endCall(callId: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/calls/${callId}/end`, {}, this.headers);
  }

  acceptCall(callId: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/calls/${callId}/accept`, {}, this.headers);
  }

  rejectCall(callId: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/calls/${callId}/reject`, {}, this.headers);
  }

  async connectHub(): Promise<void> {
    if (this.hub?.state === signalR.HubConnectionState.Connected) return;

    const hubUrl = `${baseUrl.replace(/api\/$/i, '')}hubs/communication`;
    this.hub = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, { accessTokenFactory: () => localStorage.getItem('token') || '' })
      .withAutomaticReconnect()
      .build();

    this.hub.on('ReceiveThreadMessage', (message: any) => this.messageSubject.next(message));
    this.hub.on('ExistingCallParticipants', (participants: any[]) => this.existingCallParticipantsSubject.next(participants || []));
    this.hub.on('CallParticipantJoined', (connectionId: string, displayName: string) => this.callParticipantJoinedSubject.next({ connectionId, displayName }));
    this.hub.on('CallParticipantLeft', (connectionId: string) => this.callParticipantLeftSubject.next(connectionId));
    this.hub.on('IncomingCall', (call: any) => this.incomingCallSubject.next(call));
    this.hub.on('CallAccepted', (call: any) => this.callAcceptedSubject.next(call));
    this.hub.on('CallRejected', (call: any) => this.callRejectedSubject.next(call));
    this.hub.on('ReceiveOffer', (connectionId: string, sdp: string, displayName: string) => this.offerSubject.next({ connectionId, sdp, displayName }));
    this.hub.on('ReceiveAnswer', (connectionId: string, sdp: string) => this.answerSubject.next({ connectionId, sdp }));
    this.hub.on('ReceiveIceCandidate', (connectionId: string, candidate: string, sdpMid: string | null, sdpMLineIndex: number | null) => {
      this.iceCandidateSubject.next({ connectionId, candidate, sdpMid, sdpMLineIndex });
    });
    await this.hub.start();
  }

  async disconnectHub(): Promise<void> {
    await this.hub?.stop();
  }

  async joinThread(threadId: number): Promise<void> {
    await this.connectHub();
    await this.hub?.invoke('JoinThread', threadId);
  }

  async leaveThread(threadId: number): Promise<void> {
    if (this.hub?.state === signalR.HubConnectionState.Connected) {
      await this.hub.invoke('LeaveThread', threadId);
    }
  }

  async sendThreadMessage(threadId: number, message: string): Promise<void> {
    await this.connectHub();
    await this.hub?.invoke('SendThreadMessage', threadId, message);
  }

  async joinCall(callId: number): Promise<void> {
    await this.connectHub();
    await this.hub?.invoke('JoinCall', callId);
  }

  async leaveCall(callId: number): Promise<void> {
    if (this.hub?.state === signalR.HubConnectionState.Connected) {
      await this.hub.invoke('LeaveCall', callId);
    }
  }

  async sendOffer(callId: number, targetConnectionId: string, sdp: string): Promise<void> {
    await this.connectHub();
    await this.hub?.invoke('SendOffer', callId, targetConnectionId, sdp);
  }

  async sendAnswer(callId: number, targetConnectionId: string, sdp: string): Promise<void> {
    await this.connectHub();
    await this.hub?.invoke('SendAnswer', callId, targetConnectionId, sdp);
  }

  async sendIceCandidate(callId: number, targetConnectionId: string, candidate: RTCIceCandidate): Promise<void> {
    await this.connectHub();
    await this.hub?.invoke(
      'SendIceCandidate',
      callId,
      targetConnectionId,
      candidate.candidate,
      candidate.sdpMid,
      candidate.sdpMLineIndex
    );
  }
}
